import React, {FormEvent, memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {connect} from "react-redux";
import memoizeOne from "memoize-one";
import AutoSizer from "react-virtualized-auto-sizer";
import {areEqual, FixedSizeList} from "react-window";
import NumberDisplay from "./NumberDisplay";
import {Lot, Round} from "@nextlot/core/webcast/data/data_types_definitions";
import {ROUND_STOP_TYPES_MAP, ROUND_TYPES_DISPLAY_NAMES_MAP, RoundStopTypeEnum, RoundTypeEnum} from "@nextlot/core/webcast/data/WebcastDataDomain";
import {TaggedLogger} from "@nextlot/core/utilities";
import {ReduxStateType} from "@nextlot/core/webcast/services/DataStore";
import {ReduxWebcastAuctionDataType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastAuctionData";
import {AuctionsManager, DataStore} from "@nextlot/core/webcast";
import {useAppSelector} from "@nextlot/core/webcast/hooks";
import {ActionsCreators} from "@nextlot/core/webcast/redux/actions";


function roundTypeAndLotsText(round: Round, lotsMapById: Map<number, Lot>): string {
  if (!round) {
    return ''
  }
  return ROUND_TYPES_DISPLAY_NAMES_MAP.get(round.roundType)
    + ': '
    + round.lotsIds.map(lotId => `#${lotsMapById.get(lotId).number}`).join(", ")
}


type LotListItemDataType = {
  closedRounds: Array<Round>,
  lotsMapById: Map<number, Lot>,
  setOverlayRoundId: (roundId: string) => void,
}


type LotListItemPropsType = {
  data: LotListItemDataType,
  index: number,
  style: {}
};


// React.memo acts as a PureComponent here (https://reactjs.org/blog/2018/10/23/react-v-16-6.html#reactmemo)
const LotListItem = memo(function LotListItemMemoCmp(props: LotListItemPropsType) {
  const { index, style, data: itemData } = props;
  const { closedRounds, lotsMapById, setOverlayRoundId } = itemData;
  const round: Round = closedRounds[index];
  const itemsQty = Math.max(...round.lotsIds.map(lotId => lotsMapById.get(lotId).itemsQty));
  const totalPrice = round.highestBid ? round.highestBid.value * itemsQty : null;

//we have no highestBid when the round is no sale
  const [paddleHintFormIsVisible, setPaddleHintFormIsVisible] = useState<boolean>(round.highestBid?.bidderNo === '0' && !round.paddleHint)

  // show/hide the paddle number form when the value changes
  useEffect(() => {
    setPaddleHintFormIsVisible(round.highestBid?.bidderNo === '0' && !round.paddleHint)
  }, [round.paddleHint, round.highestBid])

  const roundTypeAndLotsDisplay = useMemo(
    () => roundTypeAndLotsText(round, lotsMapById),
    [round, lotsMapById]
  )

  const highestBidIsFloor = round.highestBid?.bidderNo === '0'


  return (
    <div className="round-list__item d-flex align-items-center" style={style}>
      <div className="py-1 me-auto">
        <div className="fw-bold">
          {roundTypeAndLotsDisplay}
        </div>
        {
          round.stopType !== RoundStopTypeEnum.NO_SALE
            ? <div className="">
              <strong>{ROUND_STOP_TYPES_MAP.get(round.stopType)}</strong>
              {
                ' for '
              }
              <strong>
                <NumberDisplay valueCents={round.highestBid.value} allowZero={false} />
                {
                  (round.roundType === RoundTypeEnum.GROUP_CHOICE || round.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN || round.roundType === RoundTypeEnum.GROUP_TIMESTHEMONEY)
                    ? ' each'
                    : ''
                }
                {
                  (round.roundType === RoundTypeEnum.SINGLE_BOXLOT && (itemsQty > 1))
                    ? <span> X {itemsQty} = <NumberDisplay valueCents={totalPrice} allowZero={false} /></span>
                    : ''
                }
              </strong>
              {
                ' to '
              }
              {
                highestBidIsFloor
                  ? <FloorBidderDisplay round={round} formIsVisible={paddleHintFormIsVisible} setFormIsVisible={setPaddleHintFormIsVisible} />
                  : <span className="fw-bold">{round.highestBid.bidderDisplay}</span>
              }
            </div>

            : <div className="mb-0 text-danger fw-bold">NO SALE</div>
        }
      </div>

      {
        (round.stopType !== RoundStopTypeEnum.NO_SALE && paddleHintFormIsVisible)
        && <PaddleNumberForm round={round} onSuccess={() => setPaddleHintFormIsVisible(false)} />
      }

    </div>
  )
}, areEqual);


function FloorBidderDisplay({ round, formIsVisible, setFormIsVisible }: { round: Round, formIsVisible: boolean, setFormIsVisible: (visible: boolean) => void }) {

  if (round.paddleHint) {
    const isMissingBidderInfo = !round.paddleHintBidderInfo
      || round.paddleHintBidderInfo === 'not_found'
      || round.paddleHintBidderInfo === 'pending'

    return <>
      <span className={'cursor-pointer'} onClick={() => setFormIsVisible(true)}>
        {isMissingBidderInfo && 'FLOOR'}
        [{round.paddleHint}]
        {
          !formIsVisible && <i className="fa-regular fa-edit mx-1"></i>
        }
      </span>
      {
        round.paddleHintBidderInfo && <span> &nbsp; {round.paddleHintBidderInfo}</span>
      }
    </>
  }
  else {
    return ('FLOOR')
  }
}


function PaddleNumberForm({ round, onSuccess }: { round: Round, onSuccess?: () => void }) {
  const biddingCommBusy = useAppSelector(reduxState => reduxState.activeWebcast.wbsNetworkStatus.biddingCommBusy);
  const [paddleHintValue, setPaddleHintValue] = useState<string>('')

  // trigger callback after successfully setting the value
  useEffect(() => {
    if (paddleHintValue === round.paddleHint) {
      onSuccess?.()
    }
  }, [round.paddleHint]);

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault()
    // if the input value is not empty -> we are setting it,
    // OR if the input value is empty, but there is already a floor paddle number on the round -> we are deleting it
    if (paddleHintValue?.length || round.paddleHint?.length) {
      AuctionsManager.currentWebcastAuctionHandler.controller.sendClosedRoundUpdate(round.roundId, paddleHintValue);
      DataStore.reduxStore.dispatch(ActionsCreators.webcast.webcastClosedRoundUpdate({ roundId: round.roundId, paddleHint: paddleHintValue, paddleHintBidderInfo: 'pending' }));
    }
  }, [round, paddleHintValue]);

  return (
    <form
      className="mx-3 my-0 d-flex align-items-center"
      onSubmit={e => handleSubmit(e)}
    >
      <input
        id={`input-floor-paddle-number-round-${round.roundId}`}
        placeholder={'Paddle #'}
        type="text"
        size={10}
        className="form-control"
        value={paddleHintValue}
        onChange={(e) => setPaddleHintValue(e.target.value)}
        disabled={biddingCommBusy}
      />
      <button
        type="submit"
        className="btn btn-secondary btn-sm mx-1"
        disabled={biddingCommBusy}
      >Set
      </button>
    </form>
  )
}


type ClosedRoundBiddingHistoryProps = {
  round: Round,
  roundTypeAndLotsDisplay: string
  onClose: () => void,
}

// function ClosedRoundBiddingHistory({ round, roundTypeAndLotsDisplay, onClose }: ClosedRoundBiddingHistoryProps) {
//   const bidsHistoryMostRecent = round.bidsHistory.toReversed()
//   const highestBidIndex = bidsHistoryMostRecent.findIndex(bid => round.highestBid.bidderNo === bid.bidderNo && round.highestBid.value === bid.value)
//   const biddingCommBusy = useAppSelector(reduxState => reduxState.activeWebcast.wbsNetworkStatus.biddingCommBusy);
//
//   const [selectedHighestBidIndex, setSelectedHighestBidIndex] = useState<number>(highestBidIndex)
//
//   function changeLeadingBid() {
//     const newLeadingBid = bidsHistoryMostRecent[selectedHighestBidIndex]
//     AuctionsManager.currentWebcastAuctionHandler.controller.sendClosedRoundUpdate(round.roundId, newLeadingBid.value, newLeadingBid.bidderNo)
//
//     // TODO remove after server implementation
//     setTimeout(() => {
//       const payload: ClosedRoundUpdateActionPayloadType = {
//         roundId: round.roundId,
//         leadingBidAmountCents: newLeadingBid.value,
//         leadingBidBidderNumber: newLeadingBid.bidderNo,
//       }
//       DataStore.reduxStore.dispatch(ActionsCreators.webcast.webcastClosedRoundUpdate(payload));
//       DataStore.reduxStore.dispatch(ActionsCreators.webcast.wbsCommStateUpdate({ channel: ChannelEnum.bidding, newStatus: WbsCommStatesEnum.WBS_COMM_STATUS_IDLE }))
//     }, 100)
//   }
//
//   return (
//     <div className="d-flex flex-column h-100">
//       <div className="clerk-lots-list__actions-header-container flex-shrink-0 py-3 px-3 d-flex align-items-center justify-content-between">
//         <h5 className="mb-0">Bidding history - {roundTypeAndLotsDisplay}</h5>
//         <div className="d-flex align-items-center">
//           <button
//             type="button"
//             className="btn btn-success btn-sm me-3"
//             disabled={biddingCommBusy || selectedHighestBidIndex === highestBidIndex}
//             onClick={() => changeLeadingBid()}
//           >
//             Apply
//           </button>
//           <button
//             type="button"
//             className="btn-close"
//             aria-label="Close"
//             onClick={() => onClose()} // Close overlay
//           />
//         </div>
//       </div>
//       <div className="overflow-x-scroll overflow-y-auto bg-body">
//         {bidsHistoryMostRecent.map((bid, bidIndex) =>
//           <div
//             className={classNames(
//               "py-2 border-top d-flex align-items-center",
//               {
//                 'bg-success-subtle': bidIndex === highestBidIndex,
//                 'bg-light fw-bold': bidIndex === selectedHighestBidIndex && bidIndex !== highestBidIndex,
//               }
//             )}
//             key={bid.bidderNo + bid.value}
//           >
//             {/* Value */}
//             <div className={classNames("col-2 py-1 px-3 fw-bold text-end", { 'text-muted fst-italic': bidIndex < highestBidIndex })}>
//               <NumberDisplay valueCents={bid.value} allowZero={false} />
//             </div>
//
//             {/* Bidder */}
//             <div className={classNames("col-5 px-3", { 'text-muted fst-italic': bidIndex < highestBidIndex, })}>
//               {computeBidderDisplay(bid)}
//               {bidIndex === highestBidIndex && round.paddleHint && ` [${round.paddleHint}]`}
//
//               {bidIndex < highestBidIndex &&
//                   <i className="ms-2 text-danger">(Reverted)</i>
//               }
//               {bidIndex === highestBidIndex &&
//                   <span className="ms-2 text-success">(Current leading bid)</span>
//               }
//             </div>
//
//             {/* Actions */}
//             <div className="col-5 px-3 d-flex justify-content-end align-items-center">
//
//               {/*{bidIndex === highestBidIndex && bid.bidderNo === '0' && !round.floorPaddleNumber &&*/}
//               {/*    <PaddleNumberForm round={round} />*/}
//               {/*}*/}
//
//               {bidIndex === selectedHighestBidIndex && bidIndex !== highestBidIndex &&
//                   <strong className="">New leading bid</strong>
//               }
//
//               {bidIndex !== selectedHighestBidIndex &&
//                   <button
//                       type="button"
//                       className="btn btn-sm btn-outline-secondary ms-1"
//                       disabled={biddingCommBusy}
//                       onClick={() => setSelectedHighestBidIndex(bidIndex)}
//                   >
//                       Set as leading bid
//                   </button>
//               }
//
//               {/*{bidIndex === highestBidIndex && bidIndex <= bidsHistoryMostRecent.length - 1 && // current highest bid, but not first bid*/}
//               {/*    <button*/}
//               {/*        type="button"*/}
//               {/*        className="btn btn-sm btn-danger ms-1"*/}
//               {/*        disabled={biddingCommBusy}*/}
//               {/*        onClick={() => {*/}
//               {/*            const prevBid = bidsHistoryMostRecent[bidIndex + 1]*/}
//               {/*            AuctionsManager.currentWebcastAuctionHandler.controller.sendClosedRoundUpdate(round.roundId, prevBid.value, prevBid.bidderNo)*/}
//
//               {/*            // TODO remove after server implementation*/}
//               {/*            setTimeout(() => {*/}
//               {/*                const payload: ClosedRoundUpdateActionPayloadType = {*/}
//               {/*                    roundId: round.roundId,*/}
//               {/*                    leadingBidAmountCents: prevBid.value,*/}
//               {/*                    leadingBidBidderNumber: prevBid.bidderNo,*/}
//               {/*                }*/}
//               {/*                DataStore.reduxStore.dispatch(ActionsCreators.webcast.webcastClosedRoundUpdate(payload));*/}
//               {/*                DataStore.reduxStore.dispatch(ActionsCreators.webcast.wbsCommStateUpdate({ channel: ChannelEnum.bidding, newStatus: WbsCommStatesEnum.WBS_COMM_STATUS_IDLE}))*/}
//               {/*            }, 100)*/}
//               {/*        }}*/}
//               {/*    >*/}
//               {/*        Reverse ↓*/}
//               {/*    </button>*/}
//               {/*}*/}
//
//               {/*{bidIndex === highestBidIndex && bidIndex > 0 && // current highest bid, but not last bid*/}
//               {/*    <button*/}
//               {/*        type="button"*/}
//               {/*        className="btn btn-sm btn-secondary ms-1"*/}
//               {/*        disabled={biddingCommBusy}*/}
//               {/*        onClick={() => {*/}
//               {/*            const nextBid = bidsHistoryMostRecent[bidIndex - 1]*/}
//               {/*            AuctionsManager.currentWebcastAuctionHandler.controller.sendClosedRoundUpdate(round.roundId, nextBid.value, nextBid.bidderNo)*/}
//
//               {/*            // TODO remove after server implementation*/}
//               {/*            setTimeout(() => {*/}
//               {/*                const payload: ClosedRoundUpdateActionPayloadType = {*/}
//               {/*                    roundId: round.roundId,*/}
//               {/*                    leadingBidAmountCents: nextBid.value,*/}
//               {/*                    leadingBidBidderNumber: nextBid.bidderNo,*/}
//               {/*                }*/}
//               {/*                DataStore.reduxStore.dispatch(ActionsCreators.webcast.webcastClosedRoundUpdate(payload));*/}
//               {/*                DataStore.reduxStore.dispatch(ActionsCreators.webcast.wbsCommStateUpdate({ channel: ChannelEnum.bidding, newStatus: WbsCommStatesEnum.WBS_COMM_STATUS_IDLE}))*/}
//               {/*            }, 100)*/}
//               {/*        }}*/}
//               {/*    >*/}
//               {/*        Undo ↑*/}
//               {/*    </button>*/}
//               {/*}*/}
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }


const createLotsListItemData = memoizeOne((
  closedRounds: Array<Round>,
  lotsMapById: Map<number, Lot>,
  setOverlayRoundId: (roundId: string) => void,
) => ({
  closedRounds,
  lotsMapById,
  setOverlayRoundId,
}));


const _logger = TaggedLogger.get('ClerkClosedRoundsCmp');


type CmpPropsType = {
  webcastAuctionData: ReduxWebcastAuctionDataType,
};


function ClerkClosedRoundsCmp({ webcastAuctionData }: CmpPropsType) {

  const [filterValue, setFilterValue] = useState<string>('')
  const [overlayRoundId, setOverlayRoundId] = useState<string>()

  const lotsMapById = webcastAuctionData.lotsMapById

  const displayedClosedRoundsSortedMostRecent = useMemo(() => {
    if (filterValue.length > 0) {
      const filteredRounds = webcastAuctionData.closedRounds.filter(closedRound => {
        const roundLotsIds = closedRound.lotsIds;
        const closedLots = roundLotsIds.map(lotId => lotsMapById.get(lotId));
        const lotDisplayLabels = closedLots.map(closedLot => closedLot.number.toLowerCase());
        // revert to `===` instead of `.includes` below for exact matching
        return lotDisplayLabels.some(lotDisplayLabel => lotDisplayLabel.includes(filterValue.toLowerCase()))
      })
      return (filteredRounds.length > 0 ? filteredRounds : webcastAuctionData.closedRounds).toReversed()
    }
    else {
      return webcastAuctionData.closedRounds.toReversed()
    }

  }, [filterValue, webcastAuctionData.closedRounds])


  const overlayRound = overlayRoundId
    ? webcastAuctionData.closedRounds.find(round => round.roundId === overlayRoundId)
    : null;

  // Generate the overlay display string when needed
  const overlayRoundTypeAndLotsDisplay = useMemo(
    () => roundTypeAndLotsText(overlayRound, lotsMapById),
    [overlayRound, lotsMapById]
  )


  const handleShowOverlay = useCallback((roundId: string) => {
    setOverlayRoundId(roundId);
  }, []);

  const handleCloseOverlay = useCallback(() => {
    setOverlayRoundId(null);
  }, []);


  const itemData = createLotsListItemData(displayedClosedRoundsSortedMostRecent, lotsMapById, handleShowOverlay);


  return (
    <div className="clerk-lots-list-root-container position-relative">

      <div className="clerk-lots-list__actions-header-container">
        <div>
          Filter by Lot # &nbsp;
          <input type="text" value={filterValue} onChange={e => setFilterValue(e.target.value)} />
        </div>
      </div>


      <div className="clerk-lots-list__list-container-wrapper">
        <AutoSizer>
          {({ height, width }) => (
            <FixedSizeList
              className="clerk-lots-list__list-container"
              height={height}
              width={width}
              itemCount={displayedClosedRoundsSortedMostRecent.length}
              itemSize={50}
              itemData={itemData}
              itemKey={(index, itemData) => (itemData.closedRounds[index].roundId)}
            >
              {LotListItem}
            </FixedSizeList>
          )}
        </AutoSizer>
      </div>

    </div>
  );
}


export default connect(({ activeWebcast }: ReduxStateType) => {
  return {
    webcastAuctionData: activeWebcast.webcastAuctionData,
  };
})(ClerkClosedRoundsCmp);


